import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  Avatar,
  CircularProgress,
  Paper
} from '@mui/material';
import apiService from '../../services/api';
import { Member } from '../../types';

interface MemberSearchProps {
  value: Member | null;
  onChange: (member: Member | null) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  usePublicSearch?: boolean; // Use public search endpoint (for tablet)
}

const MemberSearch: React.FC<MemberSearchProps> = ({
  value,
  onChange,
  label = "Search Member",
  placeholder = "Search by name, email, or ID number...",
  error = false,
  helperText,
  disabled = false,
  usePublicSearch = false,
}) => {
  const [options, setOptions] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchError, setSearchError] = useState('');

  // Search function
  const searchMembers = useCallback(async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('[MemberSearch] Searching for:', searchTerm, '(usePublicSearch:', usePublicSearch, ')');

      let response;
      if (usePublicSearch) {
        // Use public search endpoint (no authentication required)
        console.log('[MemberSearch] Using public search endpoint');
        response = await apiService.searchMembersPublic({
          search: searchTerm,
          limit: 10
        });
      } else {
        // Use authenticated endpoint
        const token = localStorage.getItem('token');
        console.log('[MemberSearch] Using authenticated search, token available:', !!token);
        response = await apiService.getMembers({
          search: searchTerm,
          limit: 10,
          active: true
        });
      }

      console.log('[MemberSearch] Search response:', response);
      const members = response.data || [];
      console.log('[MemberSearch] Members found:', members.length);

      setOptions(members);
    } catch (error) {
      console.error('[MemberSearch] Error searching members:', error);
      setSearchError('Failed to search members');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [usePublicSearch]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchError(''); // Clear previous errors
      console.log('[MemberSearch] About to search for:', inputValue);
      searchMembers(inputValue);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, searchMembers]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'error';
      case 'suspended': return 'warning';
      case 'current': return 'success';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  return (
    <Autocomplete
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
      }}
      options={options}
      isOptionEqualToValue={(option, value) => option._id === value._id}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.fullName || `${option.firstName || ''} ${option.lastName || ''}`.trim() || option.email || 'Unknown Member';
      }}
      loading={loading}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error || !!searchError}
          helperText={helperText || searchError}
          InputProps={{
            ...params.InputProps,
            sx: { fontSize: '20px' },
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          InputLabelProps={{
            sx: { fontSize: '18px' }
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 2, pointerEvents: 'none' }}>
            <Avatar
              src={option.profileImage || undefined}
              sx={{ mr: 2, width: 60, height: 60 }}
            >
              {`${option.firstName?.[0] || ''}${option.lastName?.[0] || ''}`}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {option.fullName || `${option.firstName} ${option.lastName}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '16px' }}>
                {option.email} • ID: {option.idNumber}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={option.membershipType || 'Unknown'}
                  size="medium"
                  variant="outlined"
                  sx={{ fontSize: '14px' }}
                />
                <Chip
                  label={option.membershipStatus || 'Unknown'}
                  size="medium"
                  color={getStatusColor(option.membershipStatus || 'inactive') as any}
                  sx={{ fontSize: '14px' }}
                />
                <Chip
                  label={option.paymentStatus || 'Unknown'}
                  size="medium"
                  color={getStatusColor(option.paymentStatus || 'unknown') as any}
                  sx={{ fontSize: '14px' }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option._id}
            label={option.fullName || `${option.firstName} ${option.lastName}`}
            avatar={
              <Avatar src={option.profileImage || undefined}>
                {`${option.firstName?.[0] || ''}${option.lastName?.[0] || ''}`}
              </Avatar>
            }
          />
        ))
      }
      PaperComponent={(props) => (
        <Paper {...props} sx={{ mt: 1, maxHeight: 500, overflow: 'auto', fontSize: '18px' }} />
      )}
      noOptionsText={
        loading 
          ? "Searching..." 
          : inputValue.length < 2 
            ? "Type at least 2 characters to search..." 
            : "No members found"
      }
      filterOptions={(x) => x} // Disable built-in filtering since we handle it on the server
    />
  );
};

export default MemberSearch;
